/**
 * Main Application Component
 *
 * Manages navigation between Canvas and AI Workflows modes
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Route, useNavigate, useSearchParams } from 'react-router-dom';
import NavigationTabs from './NavigationTabs';
import AIWorkflowDashboard from './AIWorkflowDashboard';
import AdminDashboard from './AdminDashboard';
import AIWorkflowTemplateBrowser from './AIWorkflowTemplateBrowser';
import AIWorkflowQuickModal from './AIWorkflowQuickModal';
import FileBrowser from './FileBrowser';
import ToolsCatalogPage from './ToolsCatalogPage';
import FSMViewer from './FSMViewer';
import { fetchWorkflowById } from '../api/workflowsApi';
import { fetchTemplateDetail } from '../api/templatesApi';
import { useAIWorkflowDecompose } from '../hooks/useAIWorkflow';
import { useAuth } from '../context/useAuth';
// AppHeader lives in AppLayout — ActiveFlowsMenu, user menu, formatDisplayName
// all moved there as part of brainstorm-n5u8.7.

// Lazy load components for better performance
const CanvasView = React.lazy(() => import('./CanvasView'));


/**
 * Welcome banner for first-time users. Dismissible, stored in localStorage.
 */
function WelcomeBanner() {
  return null;
}


/**
 * Top-bar routes that reuse MainApp with a starting mode until each screen
 * gets its own page. Mount with key={path} so each route starts fresh.
 */
export const MODE_ROUTES = [
  { path: '/canvas', mode: 'canvas' },
  { path: '/tasks/new', mode: 'templates' },
  { path: '/runs/open', mode: 'workflows-history' },
];

/** The <Route> elements for MODE_ROUTES, keyed by path so each mounts fresh. */
export function modeRouteElements() {
  return MODE_ROUTES.map(({ path, mode }) => (
    <Route key={path} path={path} element={<MainApp key={path} initialMode={mode} />} />
  ));
}

/**
 * Main application component
 */
export default function MainApp({ initialMode = 'templates' }) {
  const [mode, setMode] = useState(initialMode); // Default to template browser
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showQuickWorkflowModal, setShowQuickWorkflowModal] = useState(false);
  const [workflowFromCanvas, setWorkflowFromCanvas] = useState(null);

  // Lift workflow state to MainApp so it persists across mode changes
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const [currentGraph, setCurrentGraph] = useState(null);
  const [currentMetadata, setCurrentMetadata] = useState(null);

  const { decompose } = useAIWorkflowDecompose();
  const { user } = useAuth();

  // Deep-link support: /runs?workflow=<id>&view=<visualization|history>
  // Used by ActiveFlowsMenu and ToastHost to navigate the user to a specific
  // running/completed flow without bypassing MainApp's internal mode/state.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const workflowId = searchParams.get('workflow');
    const view = searchParams.get('view');
    if (!workflowId) return;

    // If we already have this workflow loaded, just switch view.
    const targetMode =
      view === 'history' ? 'workflows-history' : 'workflows-visualization';

    const loadAndSwitch = async () => {
      try {
        const detail = await fetchWorkflowById(workflowId);
        if (!detail) {
          setMode(targetMode);
          return;
        }
        const workflow = detail.workflow || detail;
        const graph = detail.graph || workflow?.graph || null;
        const metadata = detail.metadata || {
          total_nodes: workflow?.total_nodes,
          estimated_cost: workflow?.estimated_cost,
          estimated_duration_seconds: workflow?.estimated_duration_seconds,
          risk_analysis: workflow?.risk_analysis,
        };
        setCurrentWorkflow(workflow);
        setCurrentGraph(graph);
        setCurrentMetadata(metadata);
        setMode(targetMode);
      } catch (err) {
        console.error('Failed to load workflow from URL', err);
        setMode(targetMode);
      } finally {
        // Clear the params so re-renders don't re-fetch.
        const next = new URLSearchParams(searchParams);
        next.delete('workflow');
        next.delete('view');
        setSearchParams(next, { replace: true });
      }
    };
    loadAndSwitch();
  }, [searchParams, setSearchParams]);

  // Check if user is admin (first user created)
  // TODO: Backend should provide is_admin field based on creation order
  // Temporarily enabled for all logged-in users until backend implements is_admin
  const isAdmin = user ? true : false;

  const handleModeChange = useCallback((newMode) => {
    setMode(newMode);
  }, []);

  const handleTemplateSelected = useCallback((template) => {
    setSelectedTemplate(template);
    setMode('workflows-create');
  }, []);

  const handleQuickWorkflow = useCallback((initialDescription = '') => {
    // Store initial description if provided
    if (initialDescription) {
      setWorkflowFromCanvas({ initialDescription });
    }
    setShowQuickWorkflowModal(true);
  }, []);

  // URL contract: /tasks/new?describe=<text> opens the plan modal prefilled;
  // /tasks/new?template=<id> opens that template in the create form. Each acts
  // once per value, then removes its parameter.
  const [templateError, setTemplateError] = useState(false);
  const lastDeepLink = useRef(null);
  useEffect(() => {
    const describe = searchParams.get('describe');
    const templateId = searchParams.get('template');
    if (describe === null && !templateId) {
      lastDeepLink.current = null; // param gone: the same value may act again later
      return;
    }
    const key = `${describe}|${templateId}`;
    if (lastDeepLink.current === key) return; // StrictMode / re-render before the URL update lands
    lastDeepLink.current = key;

    const next = new URLSearchParams(searchParams);
    next.delete('describe');
    next.delete('template');
    setSearchParams(next, { replace: true });

    if (describe !== null && describe.trim()) {
      handleQuickWorkflow(describe);
    }
    if (templateId) {
      fetchTemplateDetail(templateId)
        .then((template) => { if (template) handleTemplateSelected(template); })
        .catch((err) => {
          console.error('Failed to load template from URL', err);
          setTemplateError(true);
        });
    }
  }, [searchParams, setSearchParams, handleQuickWorkflow, handleTemplateSelected]);

  const handleQuickWorkflowSubmit = useCallback(async (workflowData) => {
    const result = await decompose(workflowData);

    if (result) {
      const metadata = {
        total_nodes: result.total_nodes,
        estimated_cost: result.estimated_cost,
        estimated_duration_seconds: result.estimated_duration_seconds,
        risk_analysis: result.risk_analysis
      };

      // Update workflow state
      setCurrentWorkflow(result.workflow);
      setCurrentGraph(result.graph);
      setCurrentMetadata(metadata);

      // Store the workflow data to pass to the dashboard (for backwards compatibility)
      setWorkflowFromCanvas({
        workflow: result.workflow,
        graph: result.graph,
        metadata: metadata
      });

      // Switch to workflow visualization mode
      setMode('workflows-visualization');
    }
  }, [decompose]);

  const handleWorkflowViewModeChange = useCallback((viewMode) => {
    // Map internal view modes to top-level navigation modes
    const modeMap = {
      'form': 'workflows-create',
      'visualization': 'workflows-visualization',
      'history': 'workflows-history'
    };
    setMode(modeMap[viewMode] || 'workflows-create');
  }, []);

  const handleWorkflowDataChange = useCallback((workflow, graph, metadata) => {
    setCurrentWorkflow(workflow);
    setCurrentGraph(graph);
    setCurrentMetadata(metadata);
  }, []);

  return (
    <div className="flex flex-col min-h-[calc(100dvh-4rem)] bg-paper">
      {/* Navigation tabs — page-internal mode switcher (Templates / Workflows / Files / Tools).
          The persistent brand-bar (logo, user menu, ActiveFlowsMenu) is rendered by AppLayout. */}
      <NavigationTabs currentMode={mode} onModeChange={handleModeChange} isAdmin={isAdmin} />

      {/* Onboarding Banner — shows once for new users */}
      <WelcomeBanner onGetStarted={() => handleModeChange('templates')} />

      {templateError && (
        <div role="alert" className="flex items-center justify-between gap-3 px-4 py-2 bg-hl-soft border-b border-ink text-ink text-sm font-semibold">
          <span>Couldn’t open that task.</span>
          <button type="button" aria-label="Dismiss" onClick={() => setTemplateError(false)} className="focus-ring rounded px-1">×</button>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        {mode === 'canvas' && (
          <React.Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <div className="text-lg text-gray-600">Loading canvas...</div>
              </div>
            }
          >
            <CanvasView onQuickWorkflow={handleQuickWorkflow} />
          </React.Suspense>
        )}

        {mode === 'workflows-create' && (
          <div className="h-full overflow-auto">
            <AIWorkflowDashboard
              viewMode="form"
              initialTemplate={selectedTemplate}
              initialWorkflow={workflowFromCanvas}
              currentWorkflow={currentWorkflow}
              currentGraph={currentGraph}
              currentMetadata={currentMetadata}
              onViewModeChange={handleWorkflowViewModeChange}
              onWorkflowDataChange={handleWorkflowDataChange}
            />
          </div>
        )}

        {mode === 'workflows-visualization' && (
          <div className="h-full overflow-auto">
            <AIWorkflowDashboard
              viewMode="visualization"
              initialTemplate={selectedTemplate}
              initialWorkflow={workflowFromCanvas}
              currentWorkflow={currentWorkflow}
              currentGraph={currentGraph}
              currentMetadata={currentMetadata}
              onViewModeChange={handleWorkflowViewModeChange}
              onWorkflowDataChange={handleWorkflowDataChange}
            />
          </div>
        )}

        {mode === 'workflows-history' && (
          <div className="h-full overflow-auto">
            <AIWorkflowDashboard
              viewMode="history"
              initialTemplate={selectedTemplate}
              initialWorkflow={workflowFromCanvas}
              currentWorkflow={currentWorkflow}
              currentGraph={currentGraph}
              currentMetadata={currentMetadata}
              onViewModeChange={handleWorkflowViewModeChange}
              onWorkflowDataChange={handleWorkflowDataChange}
            />
          </div>
        )}

        {mode === 'files' && (
          <div className="h-full overflow-auto">
            <FileBrowser />
          </div>
        )}

        {mode === 'templates' && (
          <div className="h-full overflow-auto">
            <AIWorkflowTemplateBrowser
              onSelectTemplate={handleTemplateSelected}
              onStartFromScratch={() => handleModeChange('workflows-create')}
            />
          </div>
        )}

        {mode === 'tools' && (
          <div className="h-full overflow-auto">
            <ToolsCatalogPage />
          </div>
        )}

        {mode === 'admin' && isAdmin && (
          <div className="h-full overflow-auto">
            <AdminDashboard />
          </div>
        )}

        {mode === 'fsm-registry' && isAdmin && (
          <div className="h-full overflow-auto">
            <FSMViewer />
          </div>
        )}
      </div>

      {/* Quick Workflow Modal */}
      <AIWorkflowQuickModal
        show={showQuickWorkflowModal}
        onClose={() => {
          setShowQuickWorkflowModal(false);
          setWorkflowFromCanvas(null);
        }}
        onSubmit={handleQuickWorkflowSubmit}
        initialDescription={workflowFromCanvas?.initialDescription || ''}
      />
    </div>
  );
}
